import { inject, Injectable } from "@angular/core";
import { environment } from "src/environment/environment";
import { RazorpayServices } from "./razorpay.services";
import { RazorpayCreateOrderDTO, RazorpayOrderItem, RazorpayTransactionInitRequestDTO,RazorpayUiOutcome } from "./razorpay.interface";
import { from, Observable, of, switchMap, throwError } from "rxjs";
import { PaymentGatewayService } from "../Payment-Gateway/paymentGateway.interface";
import { PaymentGatewayInitializeRequestDTO, PaymentGatewayInitializeResponseDTO } from "../Payment-Gateway/paymentGateway.service";

declare var Razorpay: any;

@Injectable({ providedIn: 'root' })
export class RazorpayTransactionService {

  private razorpayKey = environment.razorPayAPIKeyId;
  private razorpayService = inject(RazorpayServices);
  private paymentGatewayService = inject(PaymentGatewayService);
  
  private sdkLoaded = false;

  InitializePaymentGateway(initPayload: PaymentGatewayInitializeRequestDTO, meta:{ name: string; description: string, prefill?: any; }): Observable<void> {
    return new Observable(observer => {

      // Call backend to init transaction + create order
      this.paymentGatewayService.PaymentGateway_InitializePaymentGateway(initPayload).subscribe({
        next:  async (res: PaymentGatewayInitializeResponseDTO) => {
            try {
              await this.loadSDK();

              // 2️⃣ Open Razorpay Checkout
              const rzp = new Razorpay({
                key: this.razorpayKey,
                order_id: res.gatewayOrderId,
                amount: res.amount * 100,
                currency: res.currency,
                name: meta.name,
                description: meta.description,
                prefill: meta.prefill,
                notes: initPayload.notes,

                handler: () => {
                  // UI-level success only
                  observer.next();
                  observer.complete();
                }
              });

              rzp.on('payment.failed', () => {
                observer.error({
                  source: 'CHECKOUT',
                  message: 'Payment cancelled or failed'
                });
              });

              rzp.open();

            } catch {
              observer.error({
                source: 'SDK',
                message: 'Unable to load Razorpay'
              });
            }
          },
          error: err => observer.error(err)
        });
    });
  }

  InitializePayment(
    request: RazorpayTransactionInitRequestDTO
  ): Observable<any> {

    const orderPayload: RazorpayCreateOrderDTO = {
      amount: request.amount,
      currency: request.currency ?? 'INR',
      receipt: request.receiptNo,
      notes: {
        notes_key_1: request.notes?.note1 ?? '',
        notes_key_2: request.notes?.note2 ?? ''
      }
    };

    return this.razorpayService.Razorpay_CreateOrder(orderPayload).pipe(

      // 🔴 HANDLE RAZORPAY ORDER ERRORS
      switchMap(res => {
        if (res.isStatusCode !== 200) {
          return throwError(() => ({
            source: 'ORDER_CREATE',
            statusCode: res.isStatusCode,
            message: res.error?.description || 'Failed to create Razorpay order'
          }));
        }
        return of(res);
      }),

      // 🟡 LOAD SDK + OPEN CHECKOUT
      switchMap(order =>
        from(this.loadSDK()).pipe(
          switchMap(() => this.openCheckout(order, request.meta))
        )
      )
    );
  }

  private loadSDK(): Promise<void> {
    if (this.sdkLoaded || (window as any).Razorpay) {
      this.sdkLoaded = true;
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        this.sdkLoaded = true;
        resolve();
      };
      script.onerror = () => reject('Razorpay SDK load failed');
      document.body.appendChild(script);
    });
  }

  openCheckout(order: RazorpayOrderItem, meta: any): Observable<any> {
    return new Observable(observer => {
      const rzp = new Razorpay({
        key: this.razorpayKey,
        amount: order.amount,
        currency: order.currency,
        name: meta.name,
        description: meta.description,
        order_id: order.id,
        prefill: meta.prefill,
        handler: (response: any) => {
          observer.next(response);
          observer.complete();
        }
      });

      rzp.on('payment.failed', (err: any) => {
        observer.error({
          source: 'CHECKOUT',
          statusCode: err.error?.code,
          message: err.error?.description || 'Payment failed'
        });
      });

      rzp.open();
    });
  }

  InitializePaymentGatewayWithOutcome(
  initPayload: PaymentGatewayInitializeRequestDTO,
  meta: {
    name: string;
    description: string;
    prefill?: any;
  }
): Observable<RazorpayUiOutcome> {

  return new Observable<RazorpayUiOutcome>((observer) => {

    this.paymentGatewayService
      .PaymentGateway_InitializePaymentGateway(initPayload)
      .subscribe({

        next: async (res: PaymentGatewayInitializeResponseDTO) => {

          try {
            await this.loadSDK();

            let checkoutFinished = false;

            const rzp = new Razorpay({
              key: this.razorpayKey,
              order_id: res.gatewayOrderId,
              amount: res.amount * 100,
              currency: res.currency,

              name: meta.name,
              description: meta.description,
              prefill: meta.prefill,
              notes: initPayload.notes,

              handler: (response: any) => {

                if (checkoutFinished) {
                  return;
                }

                checkoutFinished = true;

                observer.next({
                  status: "SUBMITTED",

                  razorpayPaymentId:
                    response?.razorpay_payment_id,

                  razorpayOrderId:
                    response?.razorpay_order_id,

                  razorpaySignature:
                    response?.razorpay_signature
                });

                observer.complete();
              },

              modal: {
                ondismiss: () => {

                  if (checkoutFinished) {
                    return;
                  }

                  checkoutFinished = true;

                  observer.next({
                    status: "DISMISSED"
                  });

                  observer.complete();
                }
              }
            });


            rzp.on("payment.failed", (err: any) => {

              if (checkoutFinished) {
                return;
              }

              checkoutFinished = true;

              observer.next({
                status: "FAILED",

                code:
                  err?.error?.code,

                message:
                  err?.error?.description ||
                  "Payment failed"
              });

              observer.complete();
            });


            rzp.open();

          } catch {

            observer.next({
              status: "FAILED",
              message: "Unable to load Razorpay"
            });

            observer.complete();
          }
        },


        error: (err: any) => {

          observer.next({
            status: "FAILED",

            message:
              err?.error?.message ||
              err?.message ||
              "Unable to initialize payment"
          });

          observer.complete();
        }
      });
  });
}
}

